import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import { ReactNode, useCallback, useMemo } from "react";
import NodeStringDropdown from "./NodeStringDropdown";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import {
  addEnvironmentPositionDisplayNames,
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../EnvironmentPositionContext";

export default function NodePositionDropdown(props: {
  label: string;
  selectedPositionId: string;
  onClick: (positionId: string) => void;
  accessory?: ReactNode;
  helperText?: ReactNode;
}) {
  const { onClick, selectedPositionId } = props;
  const currentTakeLocation = useSelector(selectSelectedTake3D);
  const allPositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();

  const positionOptions = useMemo(() => {
    const contexts = getEnvironmentPositionContexts(
      allPositions ?? [],
      currentTakeLocation?.situations,
      getSituationMetadata,
    );

    return addEnvironmentPositionDisplayNames(
      contexts.flatMap((context) =>
        context.actorPositions.map((position) => ({
          id: getSituationPositionId(context, position.id),
          sourceId: position.id,
          name: position.name,
          context,
        })),
      ),
    );
  }, [allPositions, currentTakeLocation?.situations, getSituationMetadata]);

  const onClickWrapper = useCallback(
    (positionName: string) => {
      onClick(
        positionOptions.find(
          (position) => position.displayName === positionName,
        )?.id ?? "",
      );
    },
    [onClick, positionOptions],
  );

  const positionDropdown = useMemo(
    () => (
      <NodeStringDropdown
        selectedItem={
          positionOptions.find(
            (position) =>
              position.id === selectedPositionId ||
              position.sourceId === selectedPositionId,
          )?.displayName ?? ""
        }
        items={positionOptions.map((position) => position.displayName)}
        label={undefined}
        onClick={onClickWrapper}
      />
    ),
    [selectedPositionId, positionOptions, onClickWrapper],
  );

  return (
    <div className="flex flex-col gap-[8px] noDrag nowheel">
      <div className="font-medium text-creator-text text-[14px]">
        {props.label}
      </div>
      <div className="flex items-center gap-[8px]">
        <div className="flex-1 min-w-0">{positionDropdown}</div>
        {props.accessory && <div className="shrink-0">{props.accessory}</div>}
      </div>
      {props.helperText && (
        <div className="text-[12px] leading-[16px] text-creator-text-sub">
          {props.helperText}
        </div>
      )}
    </div>
  );
}
